import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Lock as LockIcon,
  PlayArrow as PlayIcon,
  CheckCircle as CheckIcon,
  Link as LinkIcon,
  Replay as ReplayIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import MatchdayStatusBadge from "./MatchdayStatusBadge";

const MatchdayList = ({
  matchdays,
  onView,
  onEdit,
  onDelete,
  onStatusChange,
  onAssignMatches,
  tournamentId,
}) => {
  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm");
    } catch {
      return "Invalid date";
    }
  };

  const getStatusAction = (status) => {
    switch (status) {
      case "upcoming":
        return { icon: <PlayIcon />, label: "Activate", nextStatus: "active" };
      case "active":
        return { icon: <LockIcon />, label: "Lock", nextStatus: "locked" };
      case "locked":
        return {
          icon: <CheckIcon />,
          label: "Complete",
          nextStatus: "completed",
        };
      case "completed":
        return {
          icon: <ReplayIcon />,
          label: "Reopen",
          nextStatus: "upcoming",
        };
      default:
        return null;
    }
  };

  const getAssignedCount = (matchday) => {
    return matchday.assignedMatches || 0;
  };

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>#</TableCell>
            <TableCell>Name</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Matches</TableCell>
            <TableCell>Date Range</TableCell>
            <TableCell>Lock Time</TableCell>
            <TableCell>Budget</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {matchdays.length === 0 ? (
            <TableRow>
              <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                <Typography color="text.secondary">
                  No matchdays created yet
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            matchdays.map((matchday) => {
              const statusAction = getStatusAction(matchday.status);
              const assignedCount = getAssignedCount(matchday);
              const isFull = assignedCount >= matchday.expectedMatchCount;

              return (
                <TableRow
                  key={matchday.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => onView(matchday.id)}
                >
                  <TableCell>{matchday.matchdayNumber}</TableCell>
                  <TableCell>{matchday.name}</TableCell>
                  <TableCell>
                    <MatchdayStatusBadge status={matchday.status} />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color={isFull ? "success.main" : "text.secondary"}
                    >
                      {assignedCount} / {matchday.expectedMatchCount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(matchday.startsAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(matchday.locksAt)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {matchday.budgetInitial != null
                        ? `£${(matchday.budgetInitial / 1_000_000).toFixed(1)}m`
                        : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Box
                      sx={{
                        display: "flex",
                        gap: 0.5,
                        justifyContent: "flex-end",
                      }}
                    >
                      <Tooltip title="View">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onView(matchday.id);
                          }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Assign Matches">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onAssignMatches(matchday.id);
                          }}
                        >
                          <LinkIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit(matchday.id);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {statusAction && (
                        <Tooltip title={statusAction.label}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onStatusChange(
                                matchday.id,
                                statusAction.nextStatus
                              );
                            }}
                          >
                            {statusAction.icon}
                          </IconButton>
                        </Tooltip>
                      )}

                      {matchday.status === "upcoming" && (
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDelete(matchday.id);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MatchdayList;
